import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';

import { CommonNumberInput } from '@/components/Common/CommonNumberInput';
import { formatPrice } from '@/utils/helper';

export type PfsScheduleColumn<T> = {
  key: keyof T & string;
  label: string;
  type: 'text' | 'currency';
  gridMd?: number;
};

type PfsScheduleTableProps<T extends { _id?: string }> = {
  title: string;
  hint?: string;
  columns: PfsScheduleColumn<T>[];
  rows: T[];
  onChange: (rows: T[]) => void;
  emptyRow: () => T;
  /** Numeric column to sum into a running total footer. */
  totalKey?: keyof T & string;
  addLabel?: string;
  readOnly?: boolean;
};

/** Generic repeatable-row editor for the PFS's Schedules A–F — one config-driven table instead of six near-identical ones. */
export function PfsScheduleTable<T extends { _id?: string }>({
  title,
  hint,
  columns,
  rows,
  onChange,
  emptyRow,
  totalKey,
  addLabel = 'Add row',
  readOnly,
}: PfsScheduleTableProps<T>) {
  const total = totalKey
    ? rows.reduce((sum, row) => sum + (Number(row[totalKey]) || 0), 0)
    : null;

  const updateCell = (idx: number, key: keyof T & string, value: unknown) => {
    const next = [...rows];
    next[idx] = { ...next[idx], [key]: value };
    onChange(next);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {hint}
        </Typography>
      )}

      {rows.map((row, idx) => (
        <Grid container spacing={1.5} key={row._id ?? idx} sx={{ mb: 1.5 }}>
          {columns.map((col) => (
            <Grid
              item
              xs={12}
              md={col.gridMd ?? 12 / (columns.length + 1)}
              key={col.key}
            >
              {col.type === 'currency' ? (
                <CommonNumberInput
                  fullWidth
                  size="small"
                  label={col.label}
                  disabled={readOnly}
                  value={row[col.key] ?? ''}
                  onChange={(e) =>
                    updateCell(idx, col.key, Number(e.target.value) || 0)
                  }
                  InputProps={{ startAdornment: '$' }}
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label={col.label}
                  disabled={readOnly}
                  value={row[col.key] ?? ''}
                  onChange={(e) => updateCell(idx, col.key, e.target.value)}
                />
              )}
            </Grid>
          ))}
          <Grid
            item
            xs={12}
            md={1}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {!readOnly && (
              <IconButton
                aria-label={`Remove ${title} row`}
                onClick={() => onChange(rows.filter((_, i) => i !== idx))}
              >
                <i className="fa-light fa-trash" />
              </IconButton>
            )}
          </Grid>
        </Grid>
      ))}

      {!readOnly && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => onChange([...rows, emptyRow()])}
          sx={{ mb: 1 }}
        >
          {addLabel}
        </Button>
      )}

      {totalKey && (
        <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
          Total: {formatPrice(total ?? 0, 0)}
        </Typography>
      )}
    </Box>
  );
}
